import { formatPaise } from '@oneapp/gst';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet } from '../components/Sheet';
import { Button, Callout } from '../components/ui';
import { importKeys, importsApi } from './api';
import { FixRowSheet } from './FixRow';

/** Uses the file import's checked rows and correction form; adding is always an explicit action. */
export function ReviewSheet({
  importId,
  onClose,
  onFinish,
  inChat = false,
}: {
  importId: string;
  inChat?: boolean;
  onClose: () => void;
  onFinish: (action: string) => void;
}) {
  const { t } = useTranslation();
  const [after, setAfter] = useState(0);
  const [edit, setEdit] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const query = useQuery({
    queryKey: importKeys.review(importId, after),
    queryFn: () => importsApi.review(importId, after),
  });
  const job = query.data?.import;
  const add = async () => {
    setBusy(true);
    setFailed(false);
    try {
      if (inChat) {
        onFinish('apply');
        return;
      }
      await importsApi.apply(importId);
      onFinish('applied');
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };
  if (edit)
    return (
      <FixRowSheet
        path={edit}
        onClose={() => setEdit(null)}
        onFinish={() => {
          setEdit(null);
          void query.refetch();
        }}
      />
    );
  return (
    <Sheet
      open
      onClose={busy ? () => {} : onClose}
      label={t('imports.review.title')}
      footer={
        <div className="sheet__buttons sheet__buttons--stack">
          {query.data?.next !== null && query.data?.next !== undefined ? (
            <Button onClick={() => setAfter(query.data?.next ?? 0)}>{t('imports.review.next')}</Button>
          ) : null}
          {after > 0 ? <Button onClick={() => setAfter(0)}>{t('imports.review.first')}</Button> : null}
          <Button
            busy={busy}
            disabled={job?.status !== 'ready' || !job.counts.readyInvoices}
            onClick={() => void add()}
          >
            {t('imports.review.add', { count: job?.counts.readyInvoices ?? 0 })}
          </Button>
        </div>
      }
    >
      <h2 className="sheet__title">{t('imports.review.title')}</h2>
      <p>{t('imports.review.check')}</p>
      {failed || query.isError ? <Callout tone="error">{t('sales.loadFailed')}</Callout> : null}
      {query.data?.rows.map((row) => (
        <section key={row.id} className="check__head">
          <h3>
            {row.fields.invoiceNumber} · {row.fields.buyerName ?? row.fields.buyerGstin ?? t('imports.review.consumer')}
          </h3>
          <p>
            {row.fields.invoiceDate} · {row.fields.description ?? row.fields.hsn} ·{' '}
            {row.fields.taxableValue === null ? '—' : formatPaise(row.fields.taxableValue)} · {row.fields.rate ?? '—'}%
          </p>
          <p>
            {row.fields.buyerGstin ?? ''} · {t('sales.form.hsn')}: {row.fields.hsn ?? '—'} · {t('sales.form.quantity')}:{' '}
            {row.fields.quantity ?? '—'} · {t('sales.form.placeOfSupply')}: {row.fields.placeOfSupply ?? '—'}
          </p>
          {row.problems.map((problem) => (
            <div key={problem.id}>
              <Callout tone="amber">{problem.message}</Callout>
              {problem.actions.includes('edit') ? (
                <Button onClick={() => setEdit(`/imports/${importId}/rows/${row.id}?problem=${problem.id}`)}>
                  {t('imports.review.fix')}
                </Button>
              ) : null}
              {(['use_fix', 'keep', 'skip'] as const)
                .filter((action) => problem.actions.includes(action))
                .map((action) => (
                  <Button
                    key={action}
                    disabled={busy}
                    onClick={async () => {
                      setBusy(true);
                      setFailed(false);
                      try {
                        await importsApi.resolve(importId, problem.id, { action });
                        await query.refetch();
                      } catch {
                        setFailed(true);
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    {t(`imports.review.${action}`)}
                  </Button>
                ))}
            </div>
          ))}
        </section>
      ))}
    </Sheet>
  );
}
